/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Node } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import MsDyn365, { RichTextComponent, Text } from '@msdyn365-commerce/core';
import * as React from 'react';
import { IMfrmWarrantyData } from './mfrm-warranty.data';
import { IMfrmWarrantyProps } from './mfrm-warranty.props.autogenerated';

export interface IMfrmWarrantyViewProps extends IMfrmWarrantyProps<IMfrmWarrantyData> {
    warrantyData: React.ReactNode;
}

export interface IState {
    showSeeMore: boolean;
    isCollapsed: boolean;
    warrantyText: string;
}

export interface IResultData {
    AttributeValueId?: number;
    DataTypeValue?: number;
    Name?: string;
    RecordId?: number;
    TextValue?: string;
}

/**
 *
 * MfrmWarranty component
 * @extends {React.PureComponent<IMfrmWarrantyProps<IMfrmWarrantyData>>}
 */
class MfrmWarranty extends React.PureComponent<IMfrmWarrantyProps<IMfrmWarrantyData>, IState> {
    private focusDiv: React.RefObject<HTMLElement>;
    constructor(props: IMfrmWarrantyProps<IMfrmWarrantyData>) {
        super(props);
        this.state = {
            showSeeMore: false,
            isCollapsed: true,
            warrantyText: ''
        };
        this.focusDiv = React.createRef();
    }
    public render(): JSX.Element | null {
        const viewProps = {
            ...this.props,
            warrantyData: this._getWarrantyData()
        };
        return this.props.renderView(viewProps);
    }

    public componentDidMount() {
        if (this.props.config.getWarrantyFromPages) {
            void this._getWarrantyContent();
        }
    }

    private _getWarrantyContent = async () => {
        const { context, data: {
            productSpecificationData: { result }
        },
            config: { backendAttribute }
        } = this.props;
        const requestContext = context && context.request;
        const params = MsDyn365.isBrowser && window.location;
        const origin = params && params.origin === 'https://localhost:4000' ? params.origin : requestContext.url.requestUrl.origin;
        const resultWarrantyData = result
            ? result.find(obj => obj.Name && obj.Name.toLowerCase().trim() === backendAttribute.toLowerCase().trim())
            : undefined;
        const pageName = this._removeSpecialCharsAndDoubleSpaces(resultWarrantyData?.TextValue);
        try {
            const res = await Msdyn365.sendRequest<any>(
                `${origin}/${pageName}`,
                'get',
                null,
                { headers: {} }
            );
            // extracting only html data between main tag
            let warrantyHtml = res.data.split('<main id="main">')[1].split('</main>')[0];
            // replacing title class with tab class
            warrantyHtml = warrantyHtml?.replace('ms-content-block__title', 'mfrm-tab-accordion__title');
            this.setState({
                warrantyText: warrantyHtml
            });
        } catch (error) {
            console.log('warrantyHtml error ==>', error);
        }
    };

    private _removeSpecialCharsAndDoubleSpaces = (input: String | undefined): String => {
        if (!input) { return ""; }
        return input?.replace(/[^\w\s]/gi, '').trim()?.replace(/\s+/g, ' ')?.replace(/\s/g, '-').toLowerCase();
    };
    private _getWarrantyData(): JSX.Element {
        const tabAccordionBaseClass = 'mfrm-tab-accordion';
        const warrantyTabClassName = this.state.showSeeMore
            ? `mfrm-tab-accordion__warranty-tab content-expanded`
            : 'mfrm-tab-accordion__warranty-tab';
        // if getWarrantyFromPages on, we will get warranty-content from pages
        if (this.props.config.getWarrantyFromPages) {
            if (this.state.warrantyText) {
                return (
                    <Node className={warrantyTabClassName} ref={this.focusDiv}>
                        {this._getSeeMore(this.state.warrantyText)}
                    </Node>
                );
            }
            return <Node className={`${tabAccordionBaseClass}__no-content`}>{this.props.resources.defaultContent}</Node>;
        }

        const {
            data: {
                productSpecificationData: { result }
            },
            config: { backendAttribute, warrantyValues }
        } = this.props;
        const resultWarrantyData = result
            ? result.find(obj => obj.Name && obj.Name.toLowerCase().trim() === backendAttribute.toLowerCase().trim())
            : undefined;
        const selectedWarranty =
            (warrantyValues &&
                resultWarrantyData &&
                warrantyValues.find(
                    warranty => warranty.warrantyName?.toLowerCase().trim() === resultWarrantyData.TextValue?.toLowerCase().trim()
                )) ||
            undefined;
        if (selectedWarranty) {
            const { warrantyTitle } = selectedWarranty;
            return (
                <Node className={warrantyTabClassName} ref={this.focusDiv}>
                    {warrantyTitle && warrantyTitle.text && (
                        <Text className={`${tabAccordionBaseClass}__title`} tag={warrantyTitle.tag || 'h2'} text={warrantyTitle.text} />
                    )}
                    {this._getSeeMore(selectedWarranty.warrantyInformation as string)}
                </Node>
            );
        }
        return <Node className={`${tabAccordionBaseClass}__no-content`}>{this.props.resources.defaultContent}</Node>;
    }

    private _toggleSeeMore = (): void => {
        this.setState({
            showSeeMore: !this.state.showSeeMore,
            isCollapsed: !this.state.isCollapsed
        });
        this.focusDiv.current?.scrollIntoView();
        MsDyn365.isBrowser && window.scroll(0, window.scrollY - 80);
    };

    private _getSeeMore(warrantyText: string): JSX.Element {
        const { showSeeMore, isCollapsed } = this.state;
        const {
            config: { seeMoreText, seeLessText, charactersCount }
        } = this.props;
        const text = warrantyText;
        const tabAccordionBaseClass = 'mfrm-tab-accordion';
        const regex = /(<([^>]+)>)/gi;
        const informationLength = text?.replace(regex, '').length;
        const entitiesSymbols = [
            '&copy;',
            '&reg;',
            '&euro;',
            '&trade;',
            '&empty;',
            '&pound;',
            '&deg;',
            '&middot;',
            '&ndash;',
            '&ldquo;',
            '&rdquo;',
            '&lt;',
            ' &gt;',
            '&lsquo;',
            '&rsquo;'
        ];

        if (charactersCount && informationLength > charactersCount) {
            let charactersLength = 0;
            let htmlStart = false;
            let lastIndex = 0;
            let entityStarted = false;
            let totalEntities = 0;

            for (let index = 0; index <= text.length; index++) {
                if (text[index] === '<' || htmlStart) {
                    htmlStart = true;
                    htmlStart = text[index] === '>' ? false : true;
                } else if (
                    (text[index] === '&' && entitiesSymbols.find(entity => entity.includes(text[index] + text[index + 1]))) ||
                    entityStarted
                ) {
                    entityStarted = true;
                    if (text[index] === ';') {
                        totalEntities = totalEntities + 1;
                        entityStarted = false;
                    }
                } else if (text[index].charCodeAt(0) !== 9 && text[index].charCodeAt(0) !== 10) {
                    charactersLength = charactersLength + 1;
                    if (charactersLength === charactersCount) {
                        lastIndex = index + 1;
                        break;
                    }
                }
            }

            const totalText = lastIndex - totalEntities;

            return (
                <Node
                    className={
                        this.state.showSeeMore
                            ? `${tabAccordionBaseClass}__content expanded`
                            : `${tabAccordionBaseClass}__content collapsed`
                    }
                >
                    {isCollapsed && (
                        <RichTextComponent
                            className={`${tabAccordionBaseClass}__content-collapsed`}
                            text={`${text.substring(0, totalText)}... `}
                        />
                    )}
                    {!isCollapsed && <RichTextComponent className={`${tabAccordionBaseClass}__content-expanded`} text={text} />}
                    {
                        <a
                            role='button'
                            aria-expanded={showSeeMore}
                            className={`${tabAccordionBaseClass}__more-cta`}
                            onClick={this._toggleSeeMore}
                        >
                            {showSeeMore ? seeLessText : seeMoreText}
                        </a>
                    }
                </Node>
            );
        } else {
            return <RichTextComponent className={`${tabAccordionBaseClass}__content`} text={text} />;
        }
    }
}

export default MfrmWarranty;
